import { ReactNode, useCallback } from "react";
import * as React from "react";
import Draggable from "react-draggable";
import { FieldValues, UseFormStateProps } from "react-hook-form";

import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper, { PaperProps } from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Breakpoint, SxProps, useTheme } from "@mui/material/styles";

import SupaDemoButton from "../supa-demo-button";
import SubmitButton from "./submit-button.component";

type ModalFormProps = {
  submitButtonLabel?: ReactNode;
  isOpen: boolean;
  submitIsDisabled?: boolean;
  showCancelButton?: boolean;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit?: () => void;
  formName?: string;
  title?: string;
  children?: ReactNode;
  hideBackdrop?: boolean;
  maxWidth?: false | Breakpoint;
  dialogActionsSx?: SxProps;
  dialogContentSx?: SxProps;
  dialogTitleSx?: SxProps;
  showBackground?: boolean;
  sx?: SxProps;
  zIndex?: number;
  closeOnBackdropClick?: boolean;
  submitButtonSx?: SxProps;
  supaDemoId?: string;
  actionsChildren?: ReactNode;
};

export type RhfModalFormProps<T extends FieldValues> = ModalFormProps &
  UseFormStateProps<T>;

export default function ModalForm<T extends FieldValues>({
  isOpen,
  submitButtonLabel = "Save",
  submitIsDisabled,
  showCancelButton = false,
  isSubmitting = false,
  onCancel,
  onSubmit,
  title = "",
  formName = "title",
  hideBackdrop = true,
  maxWidth = "sm",
  control,
  children,
  dialogActionsSx,
  dialogContentSx,
  dialogTitleSx,
  showBackground,
  submitButtonSx,
  zIndex,
  closeOnBackdropClick = false,
  supaDemoId,
  actionsChildren,
  sx
}: RhfModalFormProps<T>) {
  const { palette } = useTheme();
  const nodeRef = React.useRef(null);

  const PaperComponent = useCallback(
    (props: PaperProps) => {
      return (
        <Draggable
          handle={`#draggable-dialog-${formName}`}
          cancel={'[class*="MuiDialogContent-root"]'}
          nodeRef={nodeRef}
        >
          <Paper {...props} elevation={1} ref={nodeRef} />
        </Draggable>
      );
    },
    [formName]
  );

  const onClose = useCallback(
    (event: object, reason: string) => {
      if (isSubmitting) {
        return; // Prevent closing while submitting
      }
      if (reason !== "backdropClick" || closeOnBackdropClick) {
        onCancel();
      }
    },
    [closeOnBackdropClick, onCancel, isSubmitting]
  );
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperComponent={PaperComponent}
      fullWidth
      maxWidth={maxWidth}
      hideBackdrop={hideBackdrop}
      data-testid={`draggable-dialog-${formName}`}
      aria-labelledby={`draggable-dialog-${formName}`}
      PaperProps={{
        sx: sx,
        style: hideBackdrop ? { pointerEvents: "auto" } : undefined
      }}
      className="animate__animated animate__fadeIn animate__faster"
      disableEnforceFocus={hideBackdrop}
      disableEscapeKeyDown={isSubmitting}
      style={hideBackdrop ? { pointerEvents: "none" } : undefined}
      sx={{
        backgroundColor: showBackground ? "rgba(0,0,0,0.5)" : undefined,
        zIndex
      }}
    >
      <form id={formName}>
        <DialogTitle
          id={`draggable-dialog-${formName}`}
          sx={{
            cursor: "move",
            p: 1,
            bgcolor: palette.lightBackground,
            ...dialogTitleSx
          }}
        >
          <Stack
            flexDirection="row"
            alignContent="center"
            justifyContent="space-between"
            width="100%"
          >
            <Stack flexDirection="row" justifyContent="space-between">
              <IconButton>
                <DragIndicatorIcon />
              </IconButton>
              <Box display="flex" alignItems="center">
                <Typography variant="h5" component="div">
                  {title}
                </Typography>
              </Box>
              {supaDemoId && (
                <SupaDemoButton supaDemoId={supaDemoId} sx={{ ml: 2 }} />
              )}
            </Stack>
            <IconButton
              onClick={isSubmitting ? undefined : onCancel}
              onTouchStart={isSubmitting ? undefined : onCancel}
              disabled={isSubmitting}
              data-testid="close-modal-button"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pb: 1, ...dialogContentSx }}>
          {children}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          {actionsChildren}
          {showCancelButton && (
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          {onSubmit && (
            <SubmitButton
              submitButtonLabel={submitButtonLabel}
              formName={formName}
              submitIsDisabled={submitIsDisabled}
              isSubmitting={isSubmitting}
              onSubmit={onSubmit}
              control={control}
              sx={submitButtonSx}
            />
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
